import { useCallback, useEffect } from 'react'
import { isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'

import { StatusMessage } from 'app/common/templates-next/page-template'
import PlainButton from 'app/common/ui-next/plain-button'
import ControlsBar from 'app/common/ui-next/controls-bar'
import { AcquiringItem, RefillTarget } from 'app/dataTypes/acquiring/settings'

import RefillLoading from './RefillLoading'
import RefillItem from './RefillItem'
import BalanceView from './BalanceView'
import useStep1Items from './useStep1Items'

import styles from './Step1.module.scss';

type Props = {
  acquiringItems: Map<string, AcquiringItem>
  getUnnamedHeader: (ID: string) => string
  refillTarget: RefillTarget | null
  onPrev: () => void
  onNext: (targetID?: string) => void
}

const Step1 = ({ acquiringItems, getUnnamedHeader, refillTarget, onPrev, onNext }: Props) => {
  const { t } = useTranslation('finances')

  const { items, loading } = useStep1Items(acquiringItems)

  const handleClickItem = useCallback(
    (ID?: string) => {
      onNext(ID)
    },
    [onNext],
  )

  useEffect(() => {
    if (items.length === 1 && refillTarget === RefillTarget.Self) {
      onNext(items[0].id)
    }
  }, [items]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <RefillLoading itemsCount={acquiringItems.size || 1} />
  }

  if (isEmpty(items) && !loading) {
    // это никогда не будет показываться
    return (
      <StatusMessage skipPadding>
        {'Отсутствуют данные пользователей для данного типа пополнения'}
      </StatusMessage>
    )
  }

  return (
    <>
      {items.map(({ id, balance, header }) => (
        <RefillItem
          key={id}
          ID={id}
          header={header || getUnnamedHeader(id)}
          comment={
            typeof balance === 'number' ? (
              <BalanceView
                title={t('available_text', 'Доступно')}
                loading={loading}
                balance={balance}
              />
            ) : null
          }
          onRefillClick={handleClickItem}
        />
      ))}

      <ControlsBar className={styles.buttons}>
        <PlainButton outline onClick={onPrev}>
          {t('back_bt', 'Назад')}
        </PlainButton>
      </ControlsBar>
    </>
  )
}

export default Step1
